'use client';

import { c as _c } from "react/compiler-runtime";
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { TableHead } from './primitives/table';
import { TooltipButton } from './tooltip-button';
import { cn } from '@/utils/shad';
import { TableHeadContent } from '@/comps/table-head-content';
import { ThreeDotsMenu, UIPopoverMenuItemType } from './component-library/list/ThreeDotsMenu';
import { QueryOrderingDirection } from '@/api/generated/model';
import { ChevronDown, ChevronUp, ChevronsUpDown, Info } from 'lucide-react';
export type Sort<TKey extends string = string> = {
  by: TKey;
  direction: QueryOrderingDirection;
};

/**
 * A sortable table header cell. Clicking cycles the sort: none → desc → asc → none.
 */
export function SortableTableHead(props) {
  const $ = _c(40);
  const isActive = props.sorting?.by === props.sortByKey;
  let t0;
  if ($[0] !== isActive || $[1] !== props.sortByKey || $[2] !== props.sorting?.direction) {
    const getNextSort = () => {
      if (!isActive) {
        return {
          by: props.sortByKey,
          direction: QueryOrderingDirection.desc
        };
      }
      if (props.sorting?.direction === QueryOrderingDirection.desc) {
        return {
          by: props.sortByKey,
          direction: QueryOrderingDirection.asc
        };
      }
    };
    t0 = getNextSort();
    $[0] = isActive;
    $[1] = props.sortByKey;
    $[2] = props.sorting?.direction;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const nextSort = t0;
  let t1;
  if ($[4] !== props.className) {
    t1 = cn("text-medium w-46 pr-4 pl-3 text-nowrap text-muted-foreground underline-offset-2 hover:cursor-pointer hover:underline", props.className);
    $[4] = props.className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== nextSort || $[7] !== props) {
    t2 = ev => {
      ev.stopPropagation();
      props.onSort(nextSort);
    };
    $[6] = nextSort;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props.icon) {
    t3 = props.icon && <span className="mr-1">{props.icon}</span>;
    $[9] = props.icon;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = props.total ?? 0;
  const t5 = props.showTotal ?? false;
  let t6;
  if ($[11] !== props.title || $[12] !== t4 || $[13] !== t5) {
    t6 = <TableHeadContent title={props.title} total={t4} showTotal={t5} />;
    $[11] = props.title;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== props.subtitle) {
    t7 = <span className="text-[8px] text-darkgrey">{props.subtitle}</span>;
    $[15] = props.subtitle;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== props.title || $[18] !== props.tooltip) {
    t8 = props.tooltip && <TooltipButton content={props.tooltip}><button type="button" className="ml-1 cursor-default p-0 text-muted-foreground outline-hidden focus-visible:ring-2 focus-visible:ring-ring" aria-label={`${props.title} description`} onClick={_temp}><Info className="size-3" strokeWidth={2} /></button></TooltipButton>;
    $[17] = props.title;
    $[18] = props.tooltip;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = isActive ? props.sortByKey : "";
  const t10 = props.sorting?.direction;
  let t11;
  if ($[20] !== props.sortByKey || $[21] !== t10 || $[22] !== t9) {
    t11 = <SortableButton value={props.sortByKey} currentSort={t9} direction={t10} />;
    $[20] = props.sortByKey;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== props.totalsChip || $[25] !== t11 || $[26] !== t3 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8) {
    t12 = <span className="csv-header flex items-center text-[12px]">{t3}{t6}{t7}{t8}{t11}{props.totalsChip}</span>;
    $[24] = props.totalsChip;
    $[25] = t11;
    $[26] = t3;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== props.actions) {
    t13 = props.actions && <ThreeDotsMenu align="start" iconProps={{
      className: "text-muted-foreground hover:text-accent-foreground",
      size: 14
    }} items={props.actions} />;
    $[31] = props.actions;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t12 || $[34] !== t13) {
    t14 = <div className="flex flex-row items-center">{t12}{t13}</div>;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t1 || $[37] !== t14 || $[38] !== t2) {
    t15 = <TableHead className={t1} onClick={t2}>{t14}</TableHead>;
    $[36] = t1;
    $[37] = t14;
    $[38] = t2;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
}

/**
 * Manages single-column sort state. Cycles through: none → desc → asc → none.
 * When sort is cleared, falls back to `defaultSorting` if provided.
 */
function _temp(ev_0) {
  return ev_0.stopPropagation();
}
export function useSorting(defaultSorting) {
  const $ = _c(3);
  const defaultSortingRef = useRef(defaultSorting);
  const [sorting, setSorting] = useState(defaultSorting);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = sort => {
      setSorting(sort ?? defaultSortingRef.current);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const onSort = t0;
  let t1;
  if ($[1] !== sorting) {
    t1 = {
      sorting,
      onSort
    };
    $[1] = sorting;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
export const SortableButton = t0 => {
  const $ = _c(12);
  const {
    value,
    currentSort,
    direction,
    onClick
  } = t0;
  if (value !== currentSort) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = cn("ml-1 inline");
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== onClick || $[2] !== value) {
      t2 = <ChevronsUpDown size={12} className={t1} onClick={() => onClick?.({
        by: value,
        direction: QueryOrderingDirection.desc
      })} />;
      $[1] = onClick;
      $[2] = value;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (direction === QueryOrderingDirection.asc) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = cn("ml-1 inline");
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== onClick || $[6] !== value) {
      t2 = <ChevronUp size={12} className={t1} onClick={() => onClick?.({
        by: value,
        direction: QueryOrderingDirection.desc
      })} />;
      $[5] = onClick;
      $[6] = value;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("ml-1 inline");
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== onClick || $[10] !== value) {
    t2 = <ChevronDown size={12} className={t1} onClick={() => onClick?.({
      by: value,
      direction: QueryOrderingDirection.asc
    })} />;
    $[9] = onClick;
    $[10] = value;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};